/**
 * One-minute memory cache for dashboard reads.
 *
 * Every request helper (api.ts, creator/api.ts, auditApi.ts, …) sends its
 * calls through `withCache`. A GET answered in the last 60 seconds is served
 * from memory, so moving between pages shows the same data instantly and the
 * API isn't asked again. Rules that keep it safe:
 *
 *  - Any other method (POST, PUT, PATCH, DELETE) empties the whole cache, so
 *    after saving, deleting or running something you always see fresh data.
 *  - Answers that are still in progress (a job "running", an article
 *    "writing", a video "analysing"…) are never stored, so progress screens
 *    keep updating.
 *  - Jobs, billing, sign-in and tracking calls are never cached.
 *  - The key includes the sign-in and the workspace, so accounts and
 *    workspaces never see each other's data.
 *  - Errors are never cached. Nothing is written to disk.
 */

import { track } from "./netActivity";

const TTL_MS = 60_000;

type Entry = { at: number; data: unknown };
const store = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();

const NEVER_CACHE: RegExp[] = [/\/api\/jobs\b/, /\/api\/billing\b/, /\/api\/auth\b/, /\/api\/t\//, /\/api\/admin\b/, /\.(csv|pdf|zip|md|txt|xml|json)(\?|$)/];

// A response that describes work still going on.
const IN_PROGRESS =
  /"(?:status|state|phase)"\s*:\s*"(?:queued|running|pending|processing|planning|writing|generating|analy[sz]ing|uploading|syncing|started|in_progress|waiting|scanning|crawling)"|"(?:running|syncing)"\s*:\s*(?!null|false|0\b|\[\]|"")[^,}\]\s]/i;

function identity(): string {
  try {
    return `${window.localStorage.getItem("rankbist_token") ?? ""}|${window.localStorage.getItem("rb_workspace") ?? ""}`;
  } catch {
    return "|";
  }
}

/** Forget everything (called after any change, and on sign-out/workspace switch). */
export function clearRequestCache() {
  store.clear();
  inflight.clear();
}

export async function withCache<T>(path: string, init: RequestInit | undefined, run: () => Promise<T>): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  if (method !== "GET") {
    try {
      return await track(path, run());
    } finally {
      clearRequestCache();
    }
  }
  if (typeof window === "undefined") return run();
  if (NEVER_CACHE.some((r) => r.test(path))) return track(path, run());

  const key = `${identity()}|${path}`;
  const hit = store.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data as T;

  // Two parts of a page asking for the same thing at once share one request.
  const pending = inflight.get(key);
  if (pending) return pending as Promise<T>;

  const p = track(path, run())
    .then((data) => {
      let cacheable = true;
      try {
        cacheable = !IN_PROGRESS.test(JSON.stringify(data) ?? "");
      } catch {
        cacheable = false;
      }
      if (cacheable) store.set(key, { at: Date.now(), data });
      else store.delete(key);
      return data;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}
